import type { WorkbookColumnarSheet } from "@erp/shared-types";
import {
  lexFormula,
  tableReference,
  type FormulaToken,
  type Span,
  type TableName,
} from "./formulaLexer";
import {
  FORMULA_FUNCTIONS,
  FUNCTION_NAMES,
  argumentTypeMessage,
  converts,
  isSupportedCall,
  mistypedArgument,
  type AggregateFunctionName,
  type CallName,
  type FormulaFunctionName,
  type LookupFunctionName,
} from "./formulaFunctions";
import { readTextFormat, type TextFormat } from "./textFormats";

export {
  AGGREGATE_FUNCTIONS,
  FORMULA_FUNCTIONS,
  LOOKUP_FUNCTIONS,
  TABLE_FUNCTIONS,
  type AggregateFunctionName,
  type FormulaFunctionName,
  type LookupFunctionName,
} from "./formulaFunctions";

/** A Declared type, and the type every Formula node evaluates to. */
export type ColumnType = WorkbookColumnarSheet["columns"][number]["type"];

export type ComparisonOperator = "=" | "<>" | "<" | ">" | "<=" | ">=";
export type ArithmeticOperator = "+" | "-" | "*" | "/";
export type BinaryOperator = ComparisonOperator | ArithmeticOperator | "&";

export type ParsedFormula =
  | { kind: "column"; name: string; span: Span }
  | { kind: "text"; value: string; span: Span }
  | { kind: "number"; value: string; span: Span }
  | { kind: "boolean"; value: boolean; span: Span }
  /** A Table reference; only a lookup function's range may be one. */
  | { kind: "table"; table: TableName; column: string | null; span: Span }
  | { kind: "unary"; operator: "+" | "-"; operand: ParsedFormula; span: Span }
  | {
      kind: "binary";
      operator: BinaryOperator;
      left: ParsedFormula;
      right: ParsedFormula;
      span: Span;
    }
  | {
      kind: "call";
      name: CallName;
      args: ParsedFormula[];
      span: Span;
    };

/** A table column a lookup function reads, with its type. */
export interface LookupField {
  name: string;
  type: ColumnType;
}

/**
 * A Formula with the type of every node resolved against the Fields it reads.
 * Casts are implied, not recorded: an execution adapter casts a node to the type
 * its parent needs (`operandType` for a binary node).
 */
export type TypedFormula =
  | { kind: "column"; name: string; type: ColumnType; span: Span }
  | { kind: "text"; value: string; type: "text"; span: Span }
  | { kind: "number"; value: string; type: "decimal"; span: Span }
  | { kind: "boolean"; value: boolean; type: "boolean"; span: Span }
  | {
      kind: "unary";
      operator: "+" | "-";
      operand: TypedFormula;
      type: "decimal";
      span: Span;
    }
  | {
      kind: "binary";
      operator: BinaryOperator;
      left: TypedFormula;
      right: TypedFormula;
      /** The type both operands are cast to before the operator applies. */
      operandType: ColumnType;
      type: ColumnType;
      span: Span;
    }
  | {
      kind: "call";
      name: FormulaFunctionName;
      args: TypedFormula[];
      type: ColumnType;
      span: Span;
    }
  /**
   * XLOOKUP or VLOOKUP, resolved to one exact-match lookup: the row of table
   * `tableId` whose `matchColumn` equals `value`, read at `returnColumn`; with
   * no such row, `notFound`, or #N/A without it.
   */
  | {
      kind: "lookup";
      name: LookupFunctionName;
      value: TypedFormula;
      tableId: string;
      /** The table's name, for #N/A causes and Run failures. */
      tableName: string;
      matchColumn: LookupField;
      returnColumn: LookupField;
      notFound: TypedFormula | null;
      type: ColumnType;
      span: Span;
    }
  /**
   * SUMIFS or COUNTIFS: over the rows of table `tableId` whose every criteria
   * column equals its value, the exact sum of `sumColumn` (SUMIFS) or the
   * count of rows (COUNTIFS); 0 with no such row.
   */
  | {
      kind: "aggregate";
      name: AggregateFunctionName;
      tableId: string;
      tableName: string;
      /** SUMIFS's sum_range; null for COUNTIFS. */
      sumColumn: LookupField | null;
      criteria: { column: LookupField; value: TypedFormula }[];
      type: "decimal";
      span: Span;
    };

export type FormulaProblemCode =
  | "formula-syntax"
  | "unknown-function"
  | "qualified-reference"
  | "unknown-column"
  | "unknown-table"
  | "table-reference"
  | "lookup-mode"
  | "approximate-match"
  | "lookup-column-index"
  | "argument-type"
  | "text-format"
  | "aggregate-criteria";
export interface FormulaProblem {
  code: FormulaProblemCode;
  message: string;
  span?: Span;
}

const MAX_TOKENS = 256;
const MAX_DEPTH = 16;
const precedence: Readonly<Record<BinaryOperator, number>> = {
  "=": 1,
  "<>": 1,
  "<": 1,
  ">": 1,
  "<=": 1,
  ">=": 1,
  "&": 2,
  "+": 3,
  "-": 3,
  "*": 4,
  "/": 4,
};

function isBinaryOperator(value: string): value is BinaryOperator {
  return Object.hasOwn(precedence, value);
}

class FormulaProblemError extends Error {
  constructor(readonly problem: FormulaProblem) {
    super(problem.message);
  }
}
function fail(
  message: string,
  span?: Span,
  code: FormulaProblemCode = "formula-syntax",
): never {
  throw new FormulaProblemError({ code, message, span });
}

function invalidTokenMessage(
  token: Extract<FormulaToken, { kind: "invalid" }>,
): string {
  if (token.reason === "unterminated-text")
    return "Text is missing its closing quote.";
  if (token.reason === "unterminated-column")
    return "Column reference is missing its closing bracket.";
  return `This formula has something we can't read at position ${token.span.start + 1}. Refer to columns as [@Column].`;
}

/** Rejects anything the parser cannot read before parsing starts, as the first problem. */
function checkTokens(tokens: readonly FormulaToken[]): void {
  if (tokens.length > MAX_TOKENS) fail("Formula is too complex.");
  for (const token of tokens) {
    if (token.kind === "invalid") fail(invalidTokenMessage(token), token.span);
    if (token.kind === "qualified")
      fail(
        `'${token.qualifier}.${token.name}' refers to a column in a different input. Use this worksheet's columns ([@Column]), or add a lookup to bring that column in.`,
        token.span,
        "qualified-reference",
      );
  }
}

function joinSpans(start: Span, end: Span): Span {
  return { start: start.start, end: end.end };
}

class FormulaParser {
  private offset = 0;
  constructor(
    private readonly tokens: readonly FormulaToken[],
    private readonly length: number,
  ) {}

  parse(): ParsedFormula {
    // Depth counts nesting levels: the outermost atom is level 0.
    const result = this.expression(0, -1);
    const trailing = this.tokens[this.offset];
    if (trailing)
      fail("Unexpected trailing formula content.", {
        start: trailing.span.start,
        end: this.length,
      });
    return result;
  }

  private peek(): string {
    const token = this.tokens[this.offset];
    return token?.kind === "symbol" ? token.value : "";
  }

  private expect(value: string): Span {
    const token = this.tokens[this.offset++];
    if (token?.kind !== "symbol" || token.value !== value)
      fail(`Expected '${value}' in formula.`, token?.span);
    return token.span;
  }

  private expression(minimum: number, depth: number): ParsedFormula {
    if (depth > MAX_DEPTH) fail("Formula nesting exceeds 16 levels.");
    let left = this.atom(depth + 1);
    for (;;) {
      const operator = this.peek();
      if (!isBinaryOperator(operator) || precedence[operator] < minimum)
        return left;
      this.offset++;
      const right = this.expression(precedence[operator] + 1, depth + 1);
      left = {
        kind: "binary",
        operator,
        left,
        right,
        span: joinSpans(left.span, right.span),
      };
    }
  }

  private atom(depth: number): ParsedFormula {
    if (depth > MAX_DEPTH) fail("Formula nesting exceeds 16 levels.");
    const token = this.tokens[this.offset++];
    if (!token)
      fail("Formula is incomplete.", { start: this.length, end: this.length });
    if (token.kind === "column")
      return { kind: "column", name: token.name, span: token.span };
    if (token.kind === "text")
      return { kind: "text", value: token.value, span: token.span };
    if (token.kind === "number")
      return { kind: "number", value: token.value, span: token.span };
    if (token.kind === "name") return this.name(token, depth);
    if (token.kind === "table")
      return {
        kind: "table",
        table: token.table,
        column: token.column,
        span: token.span,
      };
    if (token.kind !== "symbol")
      fail("There's a problem with this formula.", token.span);
    return this.symbol(token, depth);
  }

  /** A parenthesised expression or a signed atom. */
  private symbol(
    token: Extract<FormulaToken, { kind: "symbol" }>,
    depth: number,
  ): ParsedFormula {
    if (token.value === "(") {
      const value = this.expression(0, depth);
      const close = this.expect(")");
      return { ...value, span: joinSpans(token.span, close) };
    }
    if (token.value === "-" || token.value === "+") {
      const operand = this.atom(depth + 1);
      return {
        kind: "unary",
        operator: token.value,
        operand,
        span: joinSpans(token.span, operand.span),
      };
    }
    fail(`Unexpected '${token.value}' in formula.`, token.span);
  }

  private name(
    token: Extract<FormulaToken, { kind: "name" }>,
    depth: number,
  ): ParsedFormula {
    if (token.value === "TRUE" || token.value === "FALSE")
      return {
        kind: "boolean",
        value: token.value === "TRUE",
        span: token.span,
      };
    const { args, close } = this.arguments(depth);
    const span = joinSpans(token.span, close);
    const name = token.value;
    if (!isSupportedCall(name, args.length))
      fail(
        `${token.value} isn't a supported function, or has the wrong number of inputs. Supported: ${FUNCTION_NAMES.join(", ")}.`,
        span,
        "unknown-function",
      );
    return { kind: "call", name, args, span };
  }

  private arguments(depth: number): { args: ParsedFormula[]; close: Span } {
    this.expect("(");
    const args: ParsedFormula[] = [];
    if (this.peek() !== ")") {
      for (;;) {
        // A call's arguments are one level in, as a parenthesis's content is.
        args.push(this.expression(0, depth));
        if (this.peek() !== ",") break;
        this.offset++;
      }
    }
    return { args, close: this.expect(")") };
  }
}

export type FormulaResult<T> =
  { ok: true; formula: T } | { ok: false; problem: FormulaProblem };

/** Parses a Formula in the bounded Workbook language. SQL text is never accepted. */
export function parseFormula(text: string): FormulaResult<ParsedFormula> {
  const tokens = lexFormula(text);
  try {
    checkTokens(tokens);
    return {
      ok: true,
      formula: new FormulaParser(tokens, text.length).parse(),
    };
  } catch (error) {
    if (error instanceof FormulaProblemError)
      return { ok: false, problem: error.problem };
    throw error;
  }
}

function comparisonType(left: ColumnType, right: ColumnType): ColumnType {
  return left === "decimal" || right === "decimal" ? "decimal" : left;
}

/** A table a Table reference may name, with the types of its columns. */
export interface FormulaTable {
  id: string;
  name: string;
  fields: readonly LookupField[];
}
/** Resolves a Table reference's table; see `resolveTable` for the reasons. */
export type FormulaTables = (
  table: TableName,
) =>
  | { ok: true; table: FormulaTable }
  | { ok: false; reason: "missing" | "unknown" | "ambiguous" };

const NO_TABLES: FormulaTables = () => ({ ok: false, reason: "unknown" });

interface TypingContext {
  fields: ReadonlyMap<string, ColumnType>;
  tables: FormulaTables;
}

function problem(
  code: FormulaProblemCode,
  message: string,
  span: Span,
): FormulaProblemError {
  return new FormulaProblemError({ code, message, span });
}

type TableNode = Extract<ParsedFormula, { kind: "table" }>;

function tableNodeText(node: TableNode): string {
  return node.table.kind === "name"
    ? tableReference(node.table.name, node.column)
    : "This table reference";
}

function resolveTableNode(
  node: TableNode,
  tables: FormulaTables,
): FormulaTable {
  const resolved = tables(node.table);
  if (resolved.ok) return resolved.table;
  const name = node.table.kind === "name" ? node.table.name : "";
  const message = {
    missing: "This formula refers to a source or worksheet that was removed.",
    unknown: `There's no source or worksheet named '${name}'. Refer to columns as [@Column], and to another table's column as Table[Column].`,
    ambiguous: `More than one source or worksheet is named '${name}'. Rename one so the reference is clear.`,
  }[resolved.reason];
  throw problem("unknown-table", message, node.span);
}

function tableField(
  table: FormulaTable,
  column: string,
  span: Span,
): LookupField {
  const field = table.fields.find((item) => item.name === column);
  if (!field)
    throw problem(
      "unknown-column",
      `Unknown column '${column}' in '${table.name}'.`,
      span,
    );
  return field;
}

/** One column of another table, as XLOOKUP's lookup_array or return_array. */
function tableColumn(
  node: ParsedFormula | undefined,
  role: string,
  context: TypingContext,
  span: Span,
  name = "XLOOKUP",
): { table: FormulaTable; field: LookupField } {
  if (node?.kind !== "table" || node.column === null)
    throw problem(
      "table-reference",
      `${name}'s ${role} must be one column of a source or worksheet, such as Prices[SKU] or 'Supplier Prices'[SKU].`,
      node?.span ?? span,
    );
  const table = resolveTableNode(node, context.tables);
  return { table, field: tableField(table, node.column, node.span) };
}

/** A lookup's type: the returned column's, or text when "if not found" is another type. */
function lookupType(
  returnColumn: LookupField,
  notFound: TypedFormula | null,
): ColumnType {
  return !notFound || notFound.type === returnColumn.type
    ? returnColumn.type
    : "text";
}

type LookupCall = Extract<ParsedFormula, { kind: "call" }> & {
  name: LookupFunctionName;
};

/** The lookup_value, which the parser's argument count guarantees. */
function lookupValue(
  value: ParsedFormula | undefined,
  node: LookupCall,
): ParsedFormula {
  if (!value)
    throw problem(
      "formula-syntax",
      `${node.name} needs a lookup_value.`,
      node.span,
    );
  return value;
}

function typeXlookup(
  node: LookupCall,
  context: TypingContext,
): Extract<TypedFormula, { kind: "lookup" }> {
  const [value, lookupArray, returnArray, ifNotFound, ...modes] = node.args;
  const [firstMode] = modes;
  const lastMode = modes.at(-1);
  if (firstMode && lastMode)
    throw problem(
      "lookup-mode",
      "XLOOKUP's match_mode and search_mode aren't supported yet: it always finds an exact match. Remove its fifth and sixth inputs.",
      { start: firstMode.span.start, end: lastMode.span.end },
    );
  const match = tableColumn(lookupArray, "lookup_array", context, node.span);
  const result = tableColumn(returnArray, "return_array", context, node.span);
  if (match.table.id !== result.table.id)
    throw problem(
      "table-reference",
      "XLOOKUP's lookup_array and return_array must be columns of the same source or worksheet.",
      returnArray?.span ?? node.span,
    );
  const notFound = ifNotFound ? typeNode(ifNotFound, context) : null;
  return {
    kind: "lookup",
    name: "XLOOKUP",
    value: typeNode(lookupValue(value, node), context),
    tableId: match.table.id,
    tableName: match.table.name,
    matchColumn: match.field,
    returnColumn: result.field,
    notFound,
    type: lookupType(result.field, notFound),
    span: node.span,
  };
}

/** VLOOKUP's range_lookup: only FALSE (or 0), exact match, is supported. */
function checkExactMatch(node: LookupCall): void {
  const range = node.args[3];
  if (
    range?.kind === "boolean"
      ? !range.value
      : range?.kind === "number" && Number(range.value) === 0
  )
    return;
  throw problem(
    "approximate-match",
    range
      ? "VLOOKUP's range_lookup must be FALSE: only exact match is supported. TRUE finds an approximate match."
      : "VLOOKUP without FALSE as its last input finds an approximate match, as spreadsheets do by default, and only exact match is supported. Add FALSE, as in VLOOKUP([@SKU], Prices, 2, FALSE).",
    range?.span ?? node.span,
  );
}

/** VLOOKUP's matched column (the table's first) and its col_index_num-th. */
function vlookupColumns(
  table: FormulaTable,
  columnIndex: ParsedFormula | undefined,
  span: Span,
): { matchColumn: LookupField; returnColumn: LookupField } {
  const position =
    columnIndex?.kind === "number" ? Number(columnIndex.value) : Number.NaN;
  const [matchColumn] = table.fields;
  const returnColumn = table.fields[position - 1];
  if (!Number.isInteger(position) || !matchColumn || !returnColumn)
    throw problem(
      "lookup-column-index",
      `VLOOKUP's col_index_num must be a whole number from 1 to ${table.fields.length}, the number of columns in '${table.name}'.`,
      columnIndex?.span ?? span,
    );
  return { matchColumn, returnColumn };
}

function typeVlookup(
  node: LookupCall,
  context: TypingContext,
): Extract<TypedFormula, { kind: "lookup" }> {
  const [value, tableArray, columnIndex] = node.args;
  if (tableArray?.kind !== "table" || tableArray.column !== null)
    throw problem(
      "table-reference",
      "VLOOKUP's table_array must be a whole source or worksheet, such as Prices or 'Supplier Prices'.",
      tableArray?.span ?? node.span,
    );
  const table = resolveTableNode(tableArray, context.tables);
  const { matchColumn, returnColumn } = vlookupColumns(
    table,
    columnIndex,
    node.span,
  );
  checkExactMatch(node);
  return {
    kind: "lookup",
    name: "VLOOKUP",
    value: typeNode(lookupValue(value, node), context),
    tableId: table.id,
    tableName: table.name,
    matchColumn,
    returnColumn,
    notFound: null,
    type: returnColumn.type,
    span: node.span,
  };
}

function typeBinary(
  node: Extract<ParsedFormula, { kind: "binary" }>,
  context: TypingContext,
): TypedFormula {
  const left = typeNode(node.left, context);
  const right = typeNode(node.right, context);
  if (node.operator === "&")
    return { ...node, left, right, operandType: "text", type: "text" };
  if (["+", "-", "*", "/"].includes(node.operator))
    return { ...node, left, right, operandType: "decimal", type: "decimal" };
  return {
    ...node,
    left,
    right,
    operandType: comparisonType(left.type, right.type),
    type: "boolean",
  };
}

function typeColumn(
  node: Extract<ParsedFormula, { kind: "column" }>,
  fields: ReadonlyMap<string, ColumnType>,
): TypedFormula {
  const type = fields.get(node.name);
  if (!type)
    throw problem(
      "unknown-column",
      `Unknown column '${node.name}'.`,
      node.span,
    );
  return { ...node, type };
}

type AggregateCall = Extract<ParsedFormula, { kind: "call" }> & {
  name: AggregateFunctionName;
};

/** Text Excel's SUMIFS reads as a comparison or a wildcard, not a value to equal. */
const CRITERIA_OPERATOR = /^\s*[<>=]/;
const CRITERIA_WILDCARD = /[*?~]/;

/**
 * The text literals a criteria is built from, in order, and whether it starts
 * with one: templates build criteria by concatenation (`">="&DATE(2026,1,1)`,
 * `"*"&[@SKU]&"*"`), not only as a plain literal.
 */
function criteriaLiterals(node: ParsedFormula): {
  literals: string[];
  leading: string | null;
} {
  if (node.kind === "text")
    return { literals: [node.value], leading: node.value };
  const parts =
    node.kind === "binary" && node.operator === "&"
      ? [node.left, node.right]
      : node.kind === "call" && node.name.toUpperCase() === "CONCAT"
        ? node.args
        : [];
  const read = parts.map(criteriaLiterals);
  return {
    literals: read.flatMap(({ literals }) => literals),
    leading: read[0]?.leading ?? null,
  };
}

/** Whether Excel would read a criteria as a comparison or a wildcard pattern, which SUMIFS/COUNTIFS here don't. */
function readsAsExcelCriteria(node: ParsedFormula): boolean {
  const { literals, leading } = criteriaLiterals(node);
  return (
    (leading !== null && CRITERIA_OPERATOR.test(leading)) ||
    literals.some((text) => CRITERIA_WILDCARD.test(text))
  );
}

/** One criteria_range, criteria pair, the range a column of `table` when given. */
function typeCriterion(
  node: AggregateCall,
  pair: readonly (ParsedFormula | undefined)[],
  table: FormulaTable | null,
  context: TypingContext,
): { table: FormulaTable; column: LookupField; value: TypedFormula } {
  const [range, value] = pair;
  const resolved = criteriaRange(node, range, table, context);
  const typed = typeNode(criteriaValue(node, value, range), context);
  // The criteria is cast to its range's type, so it must be one it converts to.
  if (!converts(typed.type, resolved.field.type))
    throw problem(
      "argument-type",
      argumentTypeMessage(
        node.name,
        { name: "criteria", type: resolved.field.type },
        typed.type,
      ),
      typed.span,
    );
  return { table: resolved.table, column: resolved.field, value: typed };
}

/** A criteria_range: a column of the aggregate's one table. */
function criteriaRange(
  node: AggregateCall,
  range: ParsedFormula | undefined,
  table: FormulaTable | null,
  context: TypingContext,
): { table: FormulaTable; field: LookupField } {
  const resolved = tableColumn(
    range,
    "criteria_range",
    context,
    node.span,
    node.name,
  );
  if (!table || resolved.table.id === table.id) return resolved;
  throw problem(
    "table-reference",
    `${node.name}'s ranges must all be columns of the same source or worksheet.`,
    range?.span ?? node.span,
  );
}

/** A criteria: present, and not text Excel would read as a comparison or wildcard. */
function criteriaValue(
  node: AggregateCall,
  value: ParsedFormula | undefined,
  range: ParsedFormula | undefined,
): ParsedFormula {
  if (!value)
    throw problem(
      "aggregate-criteria",
      `${node.name} needs a criteria after each criteria_range, as in ${node.name === "SUMIFS" ? "SUMIFS(Sales[Qty], Sales[SKU], [@SKU])" : "COUNTIFS(Sales[SKU], [@SKU])"}.`,
      range?.span ?? node.span,
    );
  if (!readsAsExcelCriteria(value)) return value;
  throw problem(
    "aggregate-criteria",
    `${node.name} matches rows whose criteria_range equals the criteria; comparisons (<, >, =) and wildcards (*, ?) aren't supported. Filter the table in a worksheet first.`,
    value.span,
  );
}

/** SUMIFS's sum_range: a Number column, or Text read as numbers. */
function sumColumn(
  node: AggregateCall,
  context: TypingContext,
): { table: FormulaTable; field: LookupField } | null {
  if (node.name !== "SUMIFS") return null;
  const sum = tableColumn(
    node.args[0],
    "sum_range",
    context,
    node.span,
    node.name,
  );
  if (sum.field.type === "decimal" || sum.field.type === "text") return sum;
  throw problem(
    "argument-type",
    argumentTypeMessage(
      "SUMIFS",
      { name: "sum_range", type: "decimal" },
      sum.field.type,
    ),
    node.args[0]?.span ?? node.span,
  );
}

function typeAggregate(
  node: AggregateCall,
  context: TypingContext,
): Extract<TypedFormula, { kind: "aggregate" }> {
  const sum = sumColumn(node, context);
  const pairs = node.args.slice(sum ? 1 : 0);
  let table = sum?.table ?? null;
  const criteria: { column: LookupField; value: TypedFormula }[] = [];
  for (let index = 0; index < pairs.length; index += 2) {
    const criterion = typeCriterion(
      node,
      pairs.slice(index, index + 2),
      table,
      context,
    );
    table = criterion.table;
    criteria.push({ column: criterion.column, value: criterion.value });
  }
  if (!table)
    throw problem(
      "aggregate-criteria",
      `${node.name} needs at least one criteria_range and criteria.`,
      node.span,
    );
  return {
    kind: "aggregate",
    name: node.name,
    tableId: table.id,
    tableName: table.name,
    sumColumn: sum?.field ?? null,
    criteria,
    type: "decimal",
    span: node.span,
  };
}

const TEXT_FORMATS_SUPPORTED =
  "It supports General; numbers as 0, 0.00, #,##0 or #,##0.00 (1 to 8 decimal places), with a trailing % for a percent; and dates of yyyy, yy, mmmm, mmm, mm, m, dddd, ddd, dd and d with - / . , or spaces between, such as yyyy-mm-dd, mm/dd/yyyy, yyyymmdd or mmm yyyy.";

/** TEXT's format_text: a text literal naming a supported format. */
function textFormat(
  node: Extract<ParsedFormula, { kind: "call" }>,
  format: TypedFormula | undefined,
): { code: string; format: TextFormat } {
  if (format?.kind !== "text")
    throw problem(
      "text-format",
      'TEXT\'s format_text must be written in the formula as text, such as TEXT([@Price], "0.00") or TEXT([@Date], "yyyy-mm-dd").',
      node.args[1]?.span ?? node.span,
    );
  const read = readTextFormat(format.value);
  if (read) return { code: format.value, format: read };
  throw problem(
    "text-format",
    `TEXT doesn't support the format "${format.value}". ${TEXT_FORMATS_SUPPORTED}`,
    format.span,
  );
}

const TEXT_VALUE_TYPES: Readonly<
  Record<TextFormat["kind"], ColumnType | null>
> = { number: "decimal", date: "date", general: null };

/** TEXT's format, which its value must suit: a Number format needs a Number, a date format a Date. */
function checkTextFormat(
  node: Extract<ParsedFormula, { kind: "call" }>,
  args: readonly TypedFormula[],
): void {
  const [value, formatArg] = args;
  const { code, format } = textFormat(node, formatArg);
  const type = TEXT_VALUE_TYPES[format.kind];
  if (value && type && !converts(value.type, type))
    throw problem(
      "argument-type",
      argumentTypeMessage(
        `TEXT`,
        { name: `value (for "${code}")`, type },
        value.type,
      ),
      value.span,
    );
}

function typeCall(
  node: Extract<ParsedFormula, { kind: "call" }>,
  context: TypingContext,
): TypedFormula {
  const { name } = node;
  if (name === "XLOOKUP") return typeXlookup({ ...node, name }, context);
  if (name === "VLOOKUP") return typeVlookup({ ...node, name }, context);
  if (name === "SUMIFS" || name === "COUNTIFS")
    return typeAggregate({ ...node, name }, context);
  const args = node.args.map((arg) => typeNode(arg, context));
  const mistyped = mistypedArgument(name, args);
  const arg = mistyped && args[mistyped.index];
  if (mistyped && arg)
    throw problem(
      "argument-type",
      argumentTypeMessage(name, mistyped.parameter, arg.type),
      arg.span,
    );
  if (name === "TEXT") checkTextFormat(node, args);
  return { ...node, name, args, type: FORMULA_FUNCTIONS[name].returns(args) };
}

function typeNode(node: ParsedFormula, context: TypingContext): TypedFormula {
  switch (node.kind) {
    case "column":
      return typeColumn(node, context.fields);
    case "text":
      return { ...node, type: "text" };
    case "number":
      return { ...node, type: "decimal" };
    case "boolean":
      return { ...node, type: "boolean" };
    case "table":
      // Resolving first reports an unknown table as that, not as misplaced.
      resolveTableNode(node, context.tables);
      throw problem(
        "table-reference",
        `${tableNodeText(node)} refers to another table, which only a lookup or a conditional aggregate can read. Use XLOOKUP, such as XLOOKUP([@SKU], Prices[SKU], Prices[Price]), or SUMIFS, such as SUMIFS(Sales[Qty], Sales[SKU], [@SKU]).`,
        node.span,
      );
    case "unary":
      return {
        ...node,
        operand: typeNode(node.operand, context),
        type: "decimal",
      };
    case "binary":
      return typeBinary(node, context);
    case "call":
      return typeCall(node, context);
  }
}

/**
 * Resolves every node's type against the Fields visible to the Formula and,
 * for lookups, the tables its Table references name.
 */
export function typeFormula(
  formula: ParsedFormula,
  fields: ReadonlyMap<string, ColumnType>,
  tables: FormulaTables = NO_TABLES,
): FormulaResult<TypedFormula> {
  try {
    return { ok: true, formula: typeNode(formula, { fields, tables }) };
  } catch (error) {
    if (error instanceof FormulaProblemError)
      return { ok: false, problem: error.problem };
    throw error;
  }
}

/** A node that reads another table: a Formula lookup or a conditional aggregate. */
export type TableRead = Extract<TypedFormula, { kind: "lookup" | "aggregate" }>;

/**
 * The nodes of a typed Formula that read another table, innermost first: a
 * lookup's `value` and `notFound` and an aggregate's criteria are read before
 * it, so an engine can join them in this order.
 */
export function formulaTableReads(formula: TypedFormula): TableRead[] {
  switch (formula.kind) {
    case "unary":
      return formulaTableReads(formula.operand);
    case "binary":
      return [
        ...formulaTableReads(formula.left),
        ...formulaTableReads(formula.right),
      ];
    case "call":
      return formula.args.flatMap(formulaTableReads);
    case "lookup":
      return [
        ...formulaTableReads(formula.value),
        ...(formula.notFound ? formulaTableReads(formula.notFound) : []),
        formula,
      ];
    case "aggregate":
      return [
        ...formula.criteria.flatMap(({ value }) => formulaTableReads(value)),
        formula,
      ];
    default:
      return [];
  }
}

/** The lookups in a typed Formula, innermost first (see `formulaTableReads`). */
export function formulaLookups(
  formula: TypedFormula,
): Extract<TypedFormula, { kind: "lookup" }>[] {
  return formulaTableReads(formula).filter((read) => read.kind === "lookup");
}
